export interface ProductEntry {
  id: string;
  name: string;
  stoneType: string;
  stoneTypeName?: string; // Display name for the stone type
  productType: "Bloco" | "Brita" | "Outro" | "Pavimento" | "Revestimento" | "Chapa" | "Outro";
  functionalUnit: {
    quantity: number;
    unit: "m3" | "t" | "m2" | "kg";
    thickness?: number; // For m2 products (espessura)
  }; // UF field from diagram
  annualProduction: number; // Quantidade produzida no ano em estudo [q]
  productRevenue: number; // Faturação do produto [fat_prod] - user input, not calculated
  materialsUsed: MaterialUsage[]; // Materiais usados na produção deste produto with quantities
  materialsUsedHydric?: MaterialUsage[]; // Economic-allocated materials for hydric footprint (when carbon uses physical)
  density: number; // Stone density from general data (dens_pedra) in t/m³
  rendimento?: number;
  espessura?: number;

  // TRANSFORMATION FIELDS:
  stoneOrigin?: "same_quarry" | "associated_company" | "external_company" | "average_factors";
  originCompanyId?: string; // If from associated company
  originProductId?: string; // Which extraction product this comes from

  // Transport from the quarry to the transformation site. Supports multiple legs
  // (e.g. import via air + land). `transports` is canonical; the single
  // transportType/transportDistance fields are kept for backward compatibility
  // with footprints saved before multi-transport support.
  transports?: TransportEntry[];
  /** @deprecated use `transports` */
  transportType?: string; // Vehicle type for transport
  /** @deprecated use `transports` */
  transportDistance?: number; // Distance in km

  // Additional transformation decision tree fields:
  isDifferentStoneType?: "yes" | "no"; // Main decision point
  comesFromAssociated?: "yes" | "no"; // If different stone type, from associated company?
  comesFromAnalyzedQuarry?: "yes" | "no"; // If same stone type, from analyzed quarry?
  comesFromAssociatedExtraction?: "yes" | "no"; // If not from analyzed quarry, from associated extraction?
  extractionProductId?: string; // Which extraction product from current company
  associatedExtractionCompanyId?: string; // Associated extraction company
  associatedExtractionProductId?: string; // Product from associated extraction company
  productNotFound?: boolean; // User indicated product not found
  associatedProductNotFound?: boolean; // User indicated associated product not found

  // Mixed activity specific fields:
  usesExternalStone?: "yes" | "no"; // For mixed companies: uses external stone from other companies?

  // Calculated values
  calculatedProductRevenue: number; // Calculated faturação do produto based on ratios
  ratioByQuantity: number; // Rácio por quantidade produzida [r_prod]
  ratioByRevenue: number; // Rácio por faturação [r_fat]

  // Carbon footprint calculations
  carbonFootprintMaterials: number; // Materiais adquiridos [pc_ext-materiais]
  carbonFootprintElectricity: number; // Eletricidade, combustíveis e resíduos [pc_ext-ecr]
  carbonFootprintElectricityOnly: number; // Eletricidade apenas [pc_ele]
  carbonFootprintFuels: number; // Combustíveis [pc_comb]
  carbonFootprintResidue: number; // Resíduos [pc_res]
  carbonFootprintTotal: number; // Total [pc_prod]

  // Hydric footprint calculations
  hydricFootprintMaterials: number; // Materiais adquiridos [ph_ext-materiais]
  hydricFootprintElectricity: number; // Eletricidade e combustíveis [ph_ext-ec]
  hydricFootprintWater: number; // Água [ph_ext-agua]
  hydricFootprintTotal: number;
  carbonFootprintTransport?: number;
  hydricFootprintTransport?: number;
  carbonFootprintTransformation?: number;
  hydricFootprintTransformation?: number;

  productFlow: "extraction" | "transformation"; // Added to distinguish product submenu
}

// A single transport leg from the quarry to the transformation site.
export interface TransportEntry {
  vehicleType: string; // setup_transportDistributions id
  distance: number; // km
}

// Material usage interface for detailed quantities
export interface MaterialUsage {
  materialId: string;
  materialName: string; // For display purposes
  quantity: number; // Single quantity value
  unit: string; // Unit from material document (e.g., "t", "€", "Unidade")
  carbon_footprint?: number; // Carbon footprint for this material (quantity * factor)
  hydric_footprint?: number; // Hydric footprint for this material (quantity * factor)
}

// Interface for footprint products from database (for origin product selection)
export interface FootprintProduct {
  id: string;
  footprint_id: string; // The footprint this product belongs to
  name: string;
  product_type: string;
  stone_type_id: string;
  stone_type_name: string;
  functional_unit: {
    quantity: number;
    unit: string;
    thickness?: number;
  };
  annual_production: number;
  product_revenue: number;
  density: number;
  carbon_footprint_total: number;
  hydric_footprint_total: number;
  ratio_by_quantity: number;
  ratio_by_revenue: number;
  materials_used: MaterialUsage[];
  created_at: Date;
  updated_at: Date;
}

export interface ProductPayload {
  entries: ProductEntry[];

  // Totals for validation
  totalAnnualProduction: number;
  totalRevenue: number;

  // Validation flags
  isProductionValid: boolean; // Check if sum equals prod_ext from general data
  isRevenueValid: boolean; // Check if sum equals fat_ext from general data

  deletedProductIds?: string[];
}

// Renamed from ConsumptionInputs to be more explicit
export interface ConsumptionData {
  materials: { 
    total_pc_mat: number; 
    total_ph_mat: number;
    entries?: any[]; // Material entries for real-time updates
  };
  electricity: { total_pc_ele: number; total_ph_ele: number };
  fuels: { total_pc_comb: number; total_ph_comb: number };
  residue: { total_pc_res: number };
  water: { total_ph_agua: number; total_ph_agua_cinzenta?: number };
}

// This is the shape for the `consumptions` object in CalculationInputs
export interface CalculationConsumptions {
  // For single-activity companies (optional)
  materials?: { 
    total_pc_mat: number; 
    total_ph_mat: number;
    entries?: any[]; // Material entries for real-time updates
  };
  electricity?: { total_pc_ele: number; total_ph_ele: number };
  fuels?: { total_pc_comb: number; total_ph_comb: number };
  residue?: { total_pc_res: number };
  water?: { total_ph_agua: number; total_ph_agua_cinzenta?: number };

  // For mixed-activity companies (optional)
  extraction?: ConsumptionData;
  transformation?: ConsumptionData;
}

export interface CalculationInputs {
  generalData: {
    // Extraction fields
    prod_ext?: number; // Total extraction production
    fat_ext?: number; // Total extraction revenue
    pvp_caro_ext?: number; // PVP of most expensive extraction product
    pvp_barato_ext?: number; // PVP of cheapest extraction product

    // Transformation fields
    prod_trsf?: number; // Total transformation production
    fat_trsf?: number; // Total transformation revenue
    pvp_caro_trsf?: number; // PVP of most expensive transformation product
    pvp_barato_trsf?: number; // PVP of cheapest transformation product
    rend?: number; // Transformation process yield

    // Common fields
    stoneTypeId: string;
    stoneDensity?: number; // Will be fetched based on stoneTypeId
    activityType?: string; // To determine which fields to use
  };

  // From consumption steps (aggregated totals)
  consumptions: CalculationConsumptions;
}

// Product type options for extraction
export const EXTRACTION_PRODUCT_TYPE_OPTIONS = [
  { value: "Bloco", label: "Bloco" },
  { value: "Brita", label: "Brita" },
  { value: "Outro", label: "Outro" },
] as const;

// Product type options for transformation
export const TRANSFORMATION_PRODUCT_TYPE_OPTIONS = [
  { value: "Pavimento", label: "Pavimento" },
  { value: "Revestimento", label: "Revestimento" },
  { value: "Chapa", label: "Chapa" },
  { value: "Outro", label: "Outro" },
] as const;

// Combined product type options (for backward compatibility)
export const PRODUCT_TYPE_OPTIONS = [
  ...EXTRACTION_PRODUCT_TYPE_OPTIONS,
  ...TRANSFORMATION_PRODUCT_TYPE_OPTIONS,
] as const;

// Functional unit options for extraction
export const EXTRACTION_FUNCTIONAL_UNIT_OPTIONS = [
  { value: "m3", label: "m³", type: "volume" },
  { value: "t", label: "t", type: "weight" },
] as const;

// Functional unit options for transformation
export const TRANSFORMATION_FUNCTIONAL_UNIT_OPTIONS = [
  { value: "m2", label: "m²", type: "area" },
  { value: "m3", label: "m³", type: "volume" },
  { value: "kg", label: "kg", type: "weight" },
] as const;

// Combined functional unit options (for backward compatibility)
export const FUNCTIONAL_UNIT_OPTIONS = [
  ...EXTRACTION_FUNCTIONAL_UNIT_OPTIONS,
  ...TRANSFORMATION_FUNCTIONAL_UNIT_OPTIONS,
] as const;

// Transport distribution interface
export interface TransportDistribution {
  id: string;
  vehicleType: string;
  fe_trans: number;
  fe_transSourceId: string;
  fh_trans: number;
  fh_transSourceId: string;
  isArchived: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export function getFunctionalUnitLabel(unit: string): string {
  const found = FUNCTIONAL_UNIT_OPTIONS.find((opt) => opt.value === unit);
  return found ? found.label : unit;
}
